import { getTimeSignature } from './meter.ts';
import type { RhythmEvent, RhythmPhrase, RhythmTuplet } from './phrase.ts';

const VALUE_TICKS: Record<string, number> = { w: 192, h: 96, q: 48, e: 24, s: 12 };

/**
 * Parse the compact rhythm text used on lesson cards into a phrase:
 *   w h q e s   whole, half, quarter, eighth, sixteenth
 *   q.          dotted
 *   rq          a rest (quarter rest)
 *   q~ q        a tie: the second note continues the first
 *   3[e e e]    a triplet (three in the time of two)
 *   |           a barline (optional, for readability)
 * Throws on anything malformed or not filling whole measures.
 */
export function parseRhythmText(timeSignature: string, text: string): RhythmPhrase {
  const ts = getTimeSignature(timeSignature);
  const events: RhythmEvent[] = [];
  const tuplets: RhythmTuplet[] = [];
  let cursor = 0;
  let tieNext = false;

  const add = (token: string, scale: number) => {
    const rest = token.startsWith('r');
    let body = rest ? token.slice(1) : token;
    const tie = body.endsWith('~');
    if (tie) body = body.slice(0, -1);
    const dotted = body.endsWith('.');
    if (dotted) body = body.slice(0, -1);
    const base = VALUE_TICKS[body];
    if (base === undefined) throw new Error(`Unknown rhythm token "${token}"`);
    const duration = base * (dotted ? 1.5 : 1) * scale;
    if (!Number.isInteger(duration)) throw new Error(`"${token}" has no whole-tick length here`);
    if (tie && rest) throw new Error('A rest cannot be tied');
    const last = events[events.length - 1];
    if (tieNext && !rest && last && !last.rest) last.duration += duration;
    else events.push({ start: cursor, duration, rest });
    tieNext = tie;
    cursor += duration;
  };

  for (const match of text.matchAll(/3\[([^\]]*)\]|(\S+)/g)) {
    if (match[1] !== undefined) {
      const start = cursor;
      for (const token of match[1].trim().split(/\s+/)) add(token, 2 / 3);
      tuplets.push({ start, duration: cursor - start });
    } else if (match[2] !== '|') {
      add(match[2]!, 1);
    }
  }
  if (tieNext) throw new Error('A tie needs a following note');
  const measures = cursor / ts.measureTicks;
  if (!Number.isInteger(measures) || measures < 1) throw new Error(`"${text}" does not fill whole measures of ${timeSignature}`);
  return { timeSignature, measures, events, ...(tuplets.length ? { tuplets } : {}) };
}
